import {
  memo,
  useEffect,
  useMemo,
  useState,
  type ChangeEvent,
  type UIEvent,
} from "react";
import { ChevronDown, Plus, Search, X } from "lucide-react";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/components/ui/popover";
import { useDebounce } from "@/hooks/useDebounce";
import { cn } from "@/lib/utils";

export interface DomainLegendItem {
  readonly domain: string;
  readonly color: string;
}

export interface DomainPickerProps {
  /** Domains currently active on the chart — always visible, backend-driven. */
  items: readonly DomainLegendItem[];
  /** Full domain catalog used to populate the "+" dropdown. */
  availableDomains: readonly string[];
  onRemoveDomain: (domain: string) => void;
  onAddDomain: (domain: string) => void;
  className?: string;
}

const SEARCH_DEBOUNCE_MS = 300;
const DOMAIN_PAGE_SIZE = 50;

const sortDomains = (domains: readonly string[]): string[] =>
  [...domains].sort((a, b) => a.localeCompare(b));

function DomainPickerComponent({
  items,
  availableDomains,
  onRemoveDomain,
  onAddDomain,
  className,
}: DomainPickerProps) {
  const [isOpen, setIsOpen] = useState(false);
  const [searchQuery, setSearchQuery] = useState("");
  const [visibleCount, setVisibleCount] = useState(DOMAIN_PAGE_SIZE);
  const debouncedQuery = useDebounce(searchQuery, SEARCH_DEBOUNCE_MS);

  const activeDomains = useMemo(
    (): ReadonlySet<string> => new Set(items.map((item) => item.domain)),
    [items],
  );

  const sortedOptions = useMemo(
    (): string[] => sortDomains(availableDomains),
    [availableDomains],
  );

  const addableDomains = useMemo(
    (): string[] =>
      sortedOptions.filter((domain) => !activeDomains.has(domain)),
    [sortedOptions, activeDomains],
  );

  const filteredAddableDomains = useMemo((): string[] => {
    const query = debouncedQuery.trim().toLowerCase();
    if (!query) return addableDomains;
    return addableDomains.filter((domain) =>
      domain.toLowerCase().includes(query),
    );
  }, [addableDomains, debouncedQuery]);

  const visibleDomains = useMemo(
    (): string[] => filteredAddableDomains.slice(0, visibleCount),
    [filteredAddableDomains, visibleCount],
  );

  useEffect(() => {
    setVisibleCount(DOMAIN_PAGE_SIZE);
  }, [debouncedQuery, isOpen]);

  const handleAddDomain = (domain: string): void => {
    onAddDomain(domain);
    setIsOpen(false);
    setSearchQuery("");
  };

  const handleOpenChange = (open: boolean): void => {
    setIsOpen(open);
    if (!open) {
      setSearchQuery("");
    }
  };

  const handleSearchChange = (event: ChangeEvent<HTMLInputElement>): void => {
    setSearchQuery(event.target.value);
  };

  const handleScroll = (event: UIEvent<HTMLDivElement>): void => {
    const target = event.currentTarget;
    const isBottom =
      target.scrollHeight - target.scrollTop <= target.clientHeight + 50;

    if (isBottom && visibleCount < filteredAddableDomains.length) {
      setVisibleCount((prev) => prev + DOMAIN_PAGE_SIZE);
    }
  };

  const showAddControl = availableDomains.length > 0;

  return (
    <div
      className={cn(
        "flex flex-1 flex-wrap items-center justify-end gap-1.5",
        className,
      )}
      role="list"
      aria-label="Domain series"
    >
      {items.map((item) => (
        <button
          key={item.domain}
          type="button"
          role="listitem"
          onClick={() => onRemoveDomain(item.domain)}
          className="inline-flex max-w-[11rem] shrink-0 items-center gap-1 rounded-full border border-transparent px-2.5 py-1 text-xs font-medium text-white shadow-sm transition-opacity hover:opacity-90 cursor-pointer"
          style={{ backgroundColor: item.color }}
          aria-pressed
          title={`${item.domain} — click to remove`}
        >
          <span className="truncate">{item.domain}</span>
          <X className="size-3 shrink-0 opacity-80" />
        </button>
      ))}

      {showAddControl ? (
        <Popover open={isOpen} onOpenChange={handleOpenChange}>
          <PopoverTrigger asChild>
            <button
              type="button"
              className="inline-flex shrink-0 items-center gap-1 rounded-full border border-dashed border-border px-2 py-1 text-xs font-medium text-muted-foreground hover:bg-muted hover:text-foreground cursor-pointer"
              aria-label="Add domain to chart"
            >
              <Plus className="size-3.5" />
              <ChevronDown className="size-3 opacity-60" />
            </button>
          </PopoverTrigger>

          <PopoverContent
            className="w-[min(300px,calc(100vw-2rem))] rounded-xl border-border p-2"
            align="end"
          >
            <div className="sticky top-0 mb-2 flex items-center gap-2 rounded-md border border-border bg-card px-3 py-2">
              <Search className="size-4 shrink-0 text-muted-foreground" />
              <input
                type="text"
                placeholder="Search domain..."
                className="flex-1 bg-transparent text-sm outline-none placeholder:text-muted-foreground"
                value={searchQuery}
                onChange={handleSearchChange}
              />
            </div>

            <div
              className="hide-scrollbar max-h-[220px] space-y-0.5 overflow-y-auto px-1"
              onScroll={handleScroll}
            >
              {filteredAddableDomains.length === 0 ? (
                <div className="py-5 text-center text-sm text-muted-foreground">
                  No more domains.
                </div>
              ) : (
                visibleDomains.map((domain) => (
                  <button
                    key={domain}
                    type="button"
                    className="group flex w-full cursor-pointer items-center justify-between gap-2 rounded-md px-2 py-1.5 text-left transition-colors hover:bg-muted"
                    onClick={() => handleAddDomain(domain)}
                  >
                    <span className="truncate text-sm text-foreground/90">
                      {domain}
                    </span>
                    <Plus className="size-3.5 shrink-0 text-muted-foreground opacity-0 transition-opacity group-hover:opacity-100 group-focus-visible:opacity-100" />
                  </button>
                ))
              )}
            </div>
          </PopoverContent>
        </Popover>
      ) : null}
    </div>
  );
}

export const DomainPicker = memo(DomainPickerComponent);
DomainPicker.displayName = "DomainPicker";
